import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Leaf, HeartPulse, Dumbbell, Sparkles, Compass, Map, Sun, UtensilsCrossed,
  Footprints, CalendarCheck, LineChart, Check, X, ArrowRight, Clock, ShieldCheck,
} from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";

const ANCHOR = "Made from your answers. Not from a ready-made meal chart.";
const SAMPLE = "/images/sample";

/* Scroll-triggered reveal (subtle, respects reduced-motion via CSS) */
const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVis(true); io.disconnect(); } },
      { threshold: 0.12, rootMargin: "0px 0px -6% 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={`db-fade ${vis ? "db-in" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const Cta = ({ children = "Create My Personalised Plan", testid, variant = "primary", className = "" }) => {
  const styles = variant === "sage"
    ? { background: "var(--db-sage)", color: "#fff" }
    : variant === "outline"
    ? { background: "transparent", color: "var(--db-forest)", border: "1.5px solid var(--db-forest)" }
    : { background: "var(--db-forest)", color: "#fff", boxShadow: "0 12px 28px -12px rgba(35,102,65,0.6)" };
  return (
    <Link
      to="/questionnaire"
      data-testid={testid}
      className={`db-cta inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-base font-semibold ${className}`}
      style={styles}
    >
      {children} <ArrowRight className="w-4 h-4" />
    </Link>
  );
};

const Eyebrow = ({ children }) => (
  <span className="inline-block text-xs font-bold uppercase tracking-[0.22em]" style={{ color: "var(--db-forest)" }}>
    {children}
  </span>
);

/* Anonymised recreation of the real DietBanao journal cover (no PII) */
const JournalCover = ({ className = "" }) => (
  <div className={`db-cover rounded-2xl p-6 flex flex-col justify-between text-white shadow-xl ${className}`} style={{ aspectRatio: "3/4" }}>
    <div className="flex items-center justify-between text-[10px] font-bold tracking-[0.2em] uppercase" style={{ opacity: 0.85 }}>
      <span>DietBanao</span><Leaf className="w-3.5 h-3.5" />
    </div>
    <div>
      <p className="text-[10px] font-semibold tracking-[0.22em] uppercase" style={{ opacity: 0.8 }}>A 30-Day Lifestyle Journal</p>
      <h4 className="db-serif text-3xl font-bold leading-tight mt-2">Crafted for<br />Sita</h4>
      <p className="text-xs mt-3 leading-relaxed" style={{ opacity: 0.85 }}>Your blueprint for the next thirty days — written with you in mind.</p>
    </div>
    <p className="text-[10px] font-semibold tracking-[0.18em] uppercase" style={{ opacity: 0.7 }}>Kota · Rajasthan</p>
  </div>
);

/* A real journal page preview (image) with a caption from the page content */
const RealPage = ({ src, alt, title, sub, delay = 0 }) => (
  <Reveal delay={delay} className="shrink-0 w-[66vw] sm:w-auto snap-center">
    <figure className="db-card-lift rounded-2xl overflow-hidden" style={{ background: "#fff", border: "1px solid var(--db-line)" }}>
      <img src={src} alt={alt} loading="lazy" width="900" height="1200" className="w-full object-cover" style={{ aspectRatio: "3/4" }} />
      <figcaption className="px-4 py-3">
        <h4 className="db-serif text-base font-bold" style={{ color: "var(--db-char)" }}>{title}</h4>
        <p className="text-xs mt-0.5" style={{ color: "var(--db-char-soft)" }}>{sub}</p>
      </figcaption>
    </figure>
  </Reveal>
);

const NAV = { background: "rgba(248,244,236,0.88)" };

const LandingPage = () => {
  return (
    <div className="min-h-screen overflow-x-hidden" style={{ fontFamily: "'Montserrat', sans-serif", background: "var(--db-ivory)", color: "var(--db-char)" }}>
      {/* ───────── Nav ───────── */}
      <header className="sticky top-0 z-50 backdrop-blur-md border-b" style={{ ...NAV, borderColor: "var(--db-line)" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-6 py-3 flex items-center justify-between">
          <img src="/images/dietbanao-logo-horizontal.webp" alt="DietBanao" width="176" height="96" className="hidden sm:block h-11 md:h-12 lg:h-14 w-auto object-contain -ml-1" /><img src="/images/dietbanao-logo-128.webp" alt="DietBanao" width="172" height="128" className="sm:hidden h-12 w-auto object-contain -ml-1" />
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/pricing" className="text-sm font-semibold hover:opacity-70 transition" style={{ color: "var(--db-char-soft)" }}>Pricing</Link>
            <Link to="/blog" className="text-sm font-semibold hover:opacity-70 transition" style={{ color: "var(--db-char-soft)" }}>Blog</Link>
            <Link to="/contact" className="text-sm font-semibold hover:opacity-70 transition" style={{ color: "var(--db-char-soft)" }}>Contact</Link>
            <Link to="/about-us" className="text-sm font-semibold hover:opacity-70 transition" style={{ color: "var(--db-char-soft)" }}>About</Link>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login" className="hidden sm:inline-block text-sm font-semibold hover:opacity-70 transition px-3 py-2" style={{ color: "var(--db-char)" }}>Login</Link>
            <Link to="/questionnaire" data-testid="nav-cta" className="db-cta inline-flex items-center px-5 py-2.5 rounded-full text-sm font-semibold text-white" style={{ background: "var(--db-forest)" }}>Get Started</Link>
          </div>
        </div>
      </header>

      {/* ───────── 1. HERO ───────── */}
      <section className="relative" data-testid="hero-section">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 pt-7 pb-14 lg:pt-20 lg:pb-24 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-16 items-center">
          <div className="max-w-xl">
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] sm:text-xs font-bold uppercase tracking-wide" style={{ background: "var(--db-forest-50)", color: "var(--db-forest)" }}>
                <Leaf className="w-3.5 h-3.5" /> Made for you · Made for Indian life
              </span>
            </Reveal>
            <Reveal delay={80}>
              <h1 className="db-serif mt-4 sm:mt-6 text-[2rem] leading-[1.08] sm:text-5xl sm:leading-[1.05] lg:text-6xl font-bold tracking-tight">
                Your body is different.<br />Your routine is different.<br /><span style={{ color: "var(--db-forest)" }}>Your plan should be too.</span>
              </h1>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-4 sm:mt-6 text-base sm:text-lg leading-relaxed" style={{ color: "var(--db-char-soft)" }}>
                Answer a few easy questions about your body, food, routine and lifestyle. DietBanao turns your answers into a personalised <strong style={{ color: "var(--db-char)" }}>30-day lifestyle guide</strong> to help you manage weight, eat better, move more and build healthier daily habits — in a way that fits your real life.
              </p>
            </Reveal>
            <Reveal delay={240}>
              <div className="mt-6"><Cta testid="hero-cta" className="w-full sm:w-auto" /></div>
              <p className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium" style={{ color: "var(--db-char-soft)" }}>
                <Clock className="w-4 h-4" style={{ color: "var(--db-sage)" }} /> Easy questions · About 12–15 minutes · Pay only when you're ready
              </p>
              <p className="mt-2 text-sm italic" style={{ color: "var(--db-forest)" }}>{ANCHOR}</p>
            </Reveal>
          </div>

          {/* Hero visual: authentic stacked journal */}
          <Reveal delay={180}>
            <div className="mx-auto w-[232px] sm:w-[288px]">
              <div className="relative">
                <img src={`${SAMPLE}/plan-31.webp`} alt="Practice and Movement section of a DietBanao lifestyle journal" fetchPriority="high" width="900" height="1273" className="absolute inset-0 w-full h-full object-cover rounded-2xl shadow-lg" style={{ zIndex: 1, transform: "rotate(-8deg) translate(-22px, 14px)" }} />
                <img src={`${SAMPLE}/plan-20.webp`} alt="Daily Rhythm section of a DietBanao lifestyle journal" fetchPriority="high" width="900" height="1273" className="absolute inset-0 w-full h-full object-cover rounded-2xl shadow-lg" style={{ zIndex: 2, transform: "rotate(8deg) translate(22px, 14px)" }} />
                <div className="relative" style={{ zIndex: 3 }}><JournalCover /></div>
              </div>
              <div className="mt-6 rounded-xl px-4 py-3 flex items-center gap-3" style={{ background: "#fff", border: "1px solid var(--db-line)" }}>
                <div className="db-stat-num text-4xl shrink-0" style={{ color: "var(--db-forest)" }}>30</div>
                <p className="text-sm leading-snug" style={{ color: "var(--db-char-soft)" }}>days, built around <strong style={{ color: "var(--db-char)" }}>your</strong> life — not a ready-made chart</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────── 2. THE BIG IDEA (editorial) ───────── */}
      <section className="py-16 lg:py-24" style={{ background: "#fff" }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-6">
          <Reveal><Eyebrow>The problem</Eyebrow></Reveal>
          <Reveal delay={60}><h2 className="db-serif mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold max-w-2xl leading-tight">One meal chart cannot work for everyone.</h2></Reveal>
          <div className="mt-12 divide-y" style={{ borderColor: "var(--db-line)" }}>
            {[
              "A desk job all day is a different routine from being on your feet.",
              "Gujarati food habits are different from South-Indian ones.",
              "Evening-craving struggles need a different approach from skipped meals.",
              "Sleeping well but barely moving is different from exercising under stress.",
            ].map((t, i) => (
              <Reveal key={i} delay={i * 70}>
                <div className="flex items-start gap-5 py-5" style={{ borderColor: "var(--db-line)" }}>
                  <span className="db-serif text-xl font-bold shrink-0 w-8" style={{ color: "var(--db-sage)" }}>{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-lg leading-relaxed" style={{ color: "var(--db-char)" }}>{t}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={200}>
            <p className="db-serif mt-10 text-2xl sm:text-3xl font-bold leading-snug max-w-2xl" style={{ color: "var(--db-forest)" }}>
              That's why DietBanao starts with you — not with a ready-made chart.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ───────── 2b. EDUCATION — most of your health ───────── */}
      <section className="py-16 lg:py-24" style={{ background: "var(--db-forest)", color: "#fff" }} data-testid="education-section">
        <div className="max-w-5xl mx-auto px-5 sm:px-6">
          <div className="max-w-2xl">
            <Reveal><h2 className="db-serif text-3xl sm:text-4xl font-bold leading-tight">Most of your health comes from how you live.</h2></Reveal>
            <Reveal delay={80}><p className="mt-4 text-lg" style={{ opacity: 0.85 }}>Not just what you eat. Not just the gym. The way you live every day matters most.</p></Reveal>
          </div>
          <div className="mt-12 grid sm:grid-cols-3 gap-px rounded-2xl overflow-hidden" style={{ background: "rgba(255,255,255,0.18)" }}>
            {[
              { n: "90%", d: "of your health issues can be addressed by your daily lifestyle.", icon: HeartPulse },
              { n: "70%", d: "can be fixed with small, everyday changes — not big crash diets.", icon: Sparkles },
              { n: "15%", d: "is exercise alone. The rest is how you eat, sleep and recover.", icon: Dumbbell },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 90}>
                <div className="h-full p-7 sm:p-8" style={{ background: "var(--db-forest)" }}>
                  <s.icon className="w-6 h-6 mb-5" style={{ color: "#fff", opacity: 0.6 }} />
                  <div className="db-stat-num text-6xl">{s.n}</div>
                  <p className="mt-3 text-sm leading-relaxed" style={{ opacity: 0.88 }}>{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={260}><p className="db-serif mt-10 text-xl sm:text-2xl font-semibold" style={{ opacity: 0.95 }}>An easy way to fix your health — start with the way you live.</p></Reveal>
        </div>
      </section>

      {/* ───────── 3. PERSONALISATION — understand your life ───────── */}
      <section className="py-16 lg:py-24" style={{ background: "var(--db-ivory)" }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div className="lg:sticky lg:top-24">
            <Reveal><Eyebrow>Step one</Eyebrow></Reveal>
            <Reveal delay={60}><h2 className="db-serif mt-3 text-3xl sm:text-4xl font-bold leading-tight">First, we understand your life.</h2></Reveal>
            <Reveal delay={120}><p className="mt-4 text-lg" style={{ color: "var(--db-char-soft)" }}>Before your guide is created, DietBanao asks you a few easy questions — it takes only about <strong style={{ color: "var(--db-char)" }}>12–15 minutes</strong>. We learn things like:</p></Reveal>
            <Reveal delay={200}>
              <p className="db-serif mt-8 text-xl font-semibold" style={{ color: "var(--db-forest)" }}>The more you tell us, the more your plan feels like yours.</p>
              <div className="mt-6"><Cta testid="personalisation-cta">Start My Plan</Cta></div>
              <p className="mt-3 text-sm italic" style={{ color: "var(--db-forest)" }}>{ANCHOR}</p>
            </Reveal>
          </div>
          <div className="grid sm:grid-cols-2 gap-x-8">
            {["Your age, height, weight and goal", "The food you normally eat", "Vegetarian or non-vegetarian preference", "Your daily work routine", "How active you are", "Your sleep habits", "Your stress and energy", "Your cravings and eating patterns", "Your meal timings", "Your everyday lifestyle challenges"].map((t, i) => (
              <Reveal key={i} delay={i * 35}>
                <div className="flex items-center gap-3 py-3 border-b" style={{ borderColor: "var(--db-line)" }}>
                  <Check className="w-4 h-4 shrink-0" style={{ color: "var(--db-forest)" }} /><span className="text-sm">{t}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── 4. WHAT'S INSIDE ───────── */}
      <section className="py-16 lg:py-24" style={{ background: "#fff" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-6">
          <Reveal className="max-w-3xl"><Eyebrow>What you receive</Eyebrow>
            <h2 className="db-serif mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">This isn't just a food chart.<br />It's your personal 30-day lifestyle guide.</h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
            {[
              { icon: Compass, t: "Your Starting Point", d: "Understand where you are today before deciding what to change." },
              { icon: Map, t: "Your Habit Map", d: "See which habits already help you and which make your goal harder." },
              { icon: Sparkles, t: "Your Personal Patterns", d: "Weight isn't only what you eat — it's when, why and how." },
              { icon: Sun, t: "Your Daily Routine", d: "A practical day from waking to bedtime: meals, movement, rest." },
              { icon: UtensilsCrossed, t: "Your Meal Plan", d: "Real meals, familiar food, built around your preferences and region." },
              { icon: Footprints, t: "Your Movement Plan", d: "Starts from your current activity level, then builds up gradually." },
              { icon: CalendarCheck, t: "Your Weekly Actions", d: "30 days split into small steps: what to focus on first, next, review." },
              { icon: LineChart, t: "Your Progress Review", d: "A Day-30 look-back at weight and the habits you worked on." },
            ].map((c, i) => (
              <Reveal key={i} delay={i * 50}>
                <div className="h-full">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: "var(--db-forest-50)" }}>
                    <c.icon className="w-5 h-5" style={{ color: "var(--db-forest)" }} />
                  </div>
                  <h3 className="db-serif text-lg font-bold">{c.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--db-char-soft)" }}>{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={200}><p className="mt-12 text-sm max-w-2xl" style={{ color: "var(--db-char-soft)" }}>The meals shown in sample plans belong to those users. <strong style={{ color: "var(--db-char)" }}>Your meals are created from your own answers.</strong></p></Reveal>
        </div>
      </section>

      {/* ───────── 5. SAMPLE GUIDE SPREAD (real pages) ───────── */}
      <section className="py-16 lg:py-24" style={{ background: "var(--db-ivory-deep)" }} data-testid="sample-section">
        <div className="max-w-6xl mx-auto px-5 sm:px-6">
          <Reveal className="max-w-3xl"><Eyebrow>Look inside</Eyebrow>
            <h2 className="db-serif mt-3 text-3xl sm:text-4xl font-bold leading-tight">See what "personalised" actually means.</h2>
            <p className="mt-4 text-lg" style={{ color: "var(--db-char-soft)" }}>Look inside a real DietBanao guide — a 48-page personalised 30-day lifestyle journal, not a one-page chart.</p>
          </Reveal>
          <div className="mt-12 flex gap-4 sm:gap-5 overflow-x-auto sm:grid sm:grid-cols-3 lg:grid-cols-5 snap-x snap-mandatory pb-4 -mx-5 px-5 sm:mx-0 sm:px-0 scrollbar-hide">
            <Reveal className="shrink-0 w-[66vw] sm:w-auto snap-center">
              <figure className="db-card-lift rounded-2xl overflow-hidden" style={{ border: "1px solid var(--db-line)" }}>
                <JournalCover className="rounded-none" />
                <figcaption className="px-4 py-3" style={{ background: "#fff" }}>
                  <h4 className="db-serif text-base font-bold">Crafted for Sita</h4>
                  <p className="text-xs mt-0.5" style={{ color: "var(--db-char-soft)" }}>Kota · Your blueprint for the next thirty days — written with you in mind.</p>
                </figcaption>
              </figure>
            </Reveal>
            <RealPage src={`${SAMPLE}/plan-you.webp`} alt="Part 1 — You, the starting point of your lifestyle journal" title="You" sub="The starting point is honest data." delay={70} />
            <RealPage src={`${SAMPLE}/plan-14.webp`} alt="Part 2 — Mindset section of your lifestyle journal" title="Mindset" sub="Before you change food, change framing." delay={140} />
            <RealPage src={`${SAMPLE}/plan-20.webp`} alt="Part 3 — Daily Rhythm section of your lifestyle journal" title="Daily Rhythm" sub="When you eat matters as much as what." delay={210} />
            <RealPage src={`${SAMPLE}/plan-31.webp`} alt="Part 4 — Practice & Movement section of your lifestyle journal" title="Practice & Movement" sub="Small actions, compounded daily." delay={280} />
          </div>
          <Reveal delay={200}>
            <div className="mt-10">
              <Cta testid="sample-cta">See My Personalised Guide</Cta>
              <p className="mt-5 text-sm max-w-2xl" style={{ color: "var(--db-char-soft)" }}>Sample shown is one person's personalised guide. Your recommendations, meals and lifestyle plan will depend on your own answers.</p>
              <p className="mt-2 text-sm italic" style={{ color: "var(--db-forest)" }}>{ANCHOR}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ───────── 6. HOW IT WORKS ───────── */}
      <section className="py-16 lg:py-24" style={{ background: "#fff" }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-6">
          <Reveal><h2 className="db-serif text-3xl sm:text-4xl font-bold text-center leading-tight">Your personalised guide in 3 simple steps</h2></Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-10 md:gap-8">
            {[
              { n: "1", t: "Answer a few easy questions", d: "Body, routine, food, activity, sleep, goals and lifestyle. About 12–15 minutes, no nutrition knowledge needed." },
              { n: "2", t: "Your guide is created", d: "Your answers come together into a personalised 30-day lifestyle guide. Not somebody else's chart. Not a copied PDF. Yours." },
              { n: "3", t: "Start your 30 days", d: "Open it on your phone or download it. Follow it day by day, make small changes, and review your progress." },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 90}>
                <div className="relative">
                  <div className="db-stat-num text-6xl" style={{ color: "var(--db-sand)" }}>{s.n}</div>
                  <h3 className="db-serif mt-3 text-xl font-bold">{s.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--db-char-soft)" }}>{s.d}</p>
                  {i < 2 && <div className="hidden md:block absolute top-7 -right-4 w-8 h-px" style={{ background: "var(--db-line)" }} />}
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={240}><div className="mt-12 text-center"><Cta testid="how-cta" /></div></Reveal>
        </div>
      </section>

      {/* ───────── 7. COMPARISON ───────── */}
      <section className="py-16 lg:py-24" style={{ background: "var(--db-ivory)" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-6">
          <Reveal><h2 className="db-serif text-3xl sm:text-4xl font-bold text-center leading-tight">Different from a normal meal chart</h2></Reveal>
          <Reveal delay={60}><p className="mt-3 text-center text-sm italic" style={{ color: "var(--db-forest)" }}>{ANCHOR}</p></Reveal>
          <div className="mt-10 rounded-2xl overflow-hidden" style={{ background: "#fff", border: "1px solid var(--db-line)" }}>
            <div className="grid grid-cols-2 text-xs font-bold uppercase tracking-wider" style={{ background: "var(--db-ivory-deep)", color: "var(--db-char-soft)" }}>
              <div className="p-4">A normal meal chart</div>
              <div className="p-4" style={{ borderLeft: "1px solid var(--db-line)", color: "var(--db-forest)" }}>With DietBanao</div>
            </div>
            {[
              ["Same advice for many people", "Built from your own answers"],
              ["Mostly tells you what to eat", "Looks at your complete lifestyle"],
              ["Ignores your daily routine", "Considers how your day actually works"],
              ["Random foods you may not eat", "Food matched to your preferences"],
              ["Big changes from Day 1", "Changes organised across 30 days"],
              ["Focuses only on weight", "Habits, food, movement and routine"],
            ].map((row, i) => (
              <div key={i} className="grid grid-cols-2 border-t" style={{ borderColor: "var(--db-line)" }}>
                <div className="flex items-start gap-2 p-4 text-sm" style={{ color: "var(--db-char-soft)" }}><X className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#B9433F" }} />{row[0]}</div>
                <div className="flex items-start gap-2 p-4 text-sm font-medium" style={{ borderLeft: "1px solid var(--db-line)" }}><Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--db-forest)" }} />{row[1]}</div>
              </div>
            ))}
          </div>
          <Reveal delay={200}><p className="db-serif mt-10 text-center text-xl sm:text-2xl font-semibold" style={{ color: "var(--db-forest)" }}>The best plan isn't the strictest plan. It's the one you can actually live with.</p></Reveal>
        </div>
      </section>

      {/* ───────── 8. YOUR ANSWERS → YOUR PLAN ───────── */}
      <section className="py-16 lg:py-24" style={{ background: "var(--db-char)", color: "#fff" }}>
        <div className="max-w-3xl mx-auto px-5 sm:px-6 text-center">
          <Reveal><h2 className="db-serif text-3xl sm:text-5xl font-bold leading-tight">Your answers go in. Your plan comes out.</h2></Reveal>
          <Reveal delay={100}><p className="mt-6 text-lg leading-relaxed" style={{ opacity: 0.82 }}>Two people of the same age and weight can still get very different guides — because one loves sweets, one sits ten hours a day, one eats dinner at 10 PM, one prefers Gujarati veg while another eats eggs or South-Indian food, one sleeps well but barely moves. DietBanao looks at the whole picture.</p></Reveal>
          <Reveal delay={200}><div className="mt-8"><Cta testid="answers-cta" variant="sage">Tell Us About Me</Cta></div></Reveal>
        </div>
      </section>

      {/* ───────── 9. INDIAN LIFE + 10. WHO IT'S FOR ───────── */}
      <section className="py-16 lg:py-24" style={{ background: "#fff" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <Reveal><h2 className="db-serif text-3xl font-bold leading-tight">Made for Indian life. Not an imaginary perfect routine.</h2></Reveal>
            <Reveal delay={80}>
              <div className="mt-6 rounded-2xl overflow-hidden" style={{ border: "1px solid var(--db-line)" }}>
                <img src={`${SAMPLE}/indian-thali-1.jpg`} alt="A home-style Indian thali with roti, dal and vegetables" loading="lazy" width="1100" height="688" className="w-full object-cover" style={{ aspectRatio: "16/10" }} />
              </div>
            </Reveal>
            <Reveal delay={140}><div className="mt-6 flex flex-wrap gap-2">
              {["Roti & rice", "Chai breaks", "Family meals", "Office lunches", "Sunday food", "Weddings", "Travel", "Different cuisines", "Different budgets", "Different work timings"].map((c, i) => (
                <span key={i} className="px-3 py-1.5 rounded-full text-sm font-medium" style={{ background: "var(--db-forest-50)", color: "var(--db-forest)" }}>{c}</span>
              ))}
            </div></Reveal>
            <Reveal delay={200}><p className="mt-6 text-base" style={{ color: "var(--db-char-soft)" }}>DietBanao starts with the life you're already living.</p></Reveal>
          </div>
          <div>
            <Reveal><h2 className="db-serif text-3xl font-bold leading-tight">DietBanao may be for you if…</h2></Reveal>
            <div className="mt-8 space-y-4">
              {["You want to lose weight but don't know where to start", "You've downloaded charts before and stopped following them", "You want to eat familiar Indian food", "You have a busy work or study routine", "You don't want to count every calorie", "You want healthier habits without changing your whole life overnight"].map((t, i) => (
                <Reveal key={i} delay={i * 45}>
                  <div className="flex items-start gap-3 pb-4 border-b" style={{ borderColor: "var(--db-line)" }}>
                    <Check className="w-5 h-5 mt-0.5 shrink-0" style={{ color: "var(--db-forest)" }} /><span className="text-base" style={{ color: "var(--db-char)" }}>{t}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ───────── 11. BEYOND WEIGHT LOSS ───────── */}
      <section className="py-16 lg:py-24" style={{ background: "var(--db-sage-soft)" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-6 text-center">
          <Reveal><h2 className="db-serif text-3xl sm:text-4xl font-bold leading-tight">The goal isn't only a smaller number on the scale.</h2></Reveal>
          <Reveal delay={80}><div className="mt-8 flex flex-wrap justify-center gap-3">
            {["Food", "Movement", "Sleep", "Routine", "Habits", "Self-awareness", "Progress"].map((t, i) => (
              <span key={i} className="px-4 py-2 rounded-full text-sm font-semibold" style={{ background: "#fff", border: "1px solid var(--db-line)", color: "var(--db-char)" }}>{t}</span>
            ))}
          </div></Reveal>
          <Reveal delay={160}><p className="db-serif mt-10 text-xl sm:text-2xl font-semibold" style={{ color: "var(--db-forest)" }}>Lose weight. Build better habits. Live healthier.</p></Reveal>
        </div>
      </section>

      {/* ───────── 12. FAQ ───────── */}
      <section className="py-16 lg:py-24" style={{ background: "#fff" }}>
        <div className="max-w-3xl mx-auto px-5 sm:px-6">
          <Reveal><h2 className="db-serif text-3xl sm:text-4xl font-bold text-center mb-10 leading-tight">Questions, answered</h2></Reveal>
          <Accordion type="single" collapsible className="w-full">
            {[
              ["What is DietBanao?", "DietBanao creates a personalised 30-day lifestyle guide based on your body, food, routine, activity and lifestyle."],
              ["Is this only a meal plan?", "No. Food is one part of your guide. Depending on your answers, your plan can also cover daily routine, habits, movement, sleep, patterns and progress tracking."],
              ["How is my plan personalised?", "You answer a few easy questions (about 12–15 minutes). Your answers are used together to understand your life and create your guide."],
              ["Will everybody get the same food plan?", "No. Your food plan depends on the preferences and lifestyle you share. The meals shown in sample plans belong to those users and are not recommendations for everyone."],
              ["Is the food Indian?", "Yes — DietBanao is designed around Indian lifestyles and food preferences. Your meals follow what you tell us, not one fixed menu."],
              ["Do I have to stop eating roti or rice?", "There's no single rule for everyone. Your guide is created from your own answers."],
              ["How long does it take?", "About 12–15 minutes of easy questions. The more you share, the more relevant your plan."],
              ["How long is the plan?", "Your DietBanao journey is designed around 30 days."],
              ["Do I need a subscription?", "No subscription required. It's a one-time payment for each plan — and if you prefer, you can choose a 3-month or 6-month option."],
              ["Is DietBanao medical treatment?", "No. DietBanao is a lifestyle and wellness guide, not a diagnosis or a replacement for medical care. For medical conditions, medicines or pregnancy, please follow your qualified healthcare professional."],
            ].map(([q, a], i) => (
              <AccordionItem key={i} value={`item-${i}`} style={{ borderColor: "var(--db-line)" }} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-base font-semibold text-left" style={{ color: "var(--db-char)" }}>{q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed" style={{ color: "var(--db-char-soft)" }}>{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ───────── 13. FINAL CTA ───────── */}
      <section className="py-16 lg:py-24" style={{ background: "var(--db-ivory)" }}>
        <div className="max-w-5xl mx-auto px-5 sm:px-6">
          <div className="rounded-3xl p-10 sm:p-16 text-center" style={{ background: "var(--db-forest)", color: "#fff" }}>
            <Reveal><h2 className="db-serif text-3xl sm:text-5xl font-bold leading-tight">A few easy questions. 30 days.<br />One plan built around you.</h2></Reveal>
            <Reveal delay={100}><p className="mt-5 max-w-lg mx-auto text-lg leading-relaxed" style={{ opacity: 0.85 }}>Stop trying to fit your life into somebody else's meal chart. Tell DietBanao about your body, food and routine — and get a lifestyle guide made around your life.</p></Reveal>
            <Reveal delay={180}>
              <div className="mt-8 flex justify-center">
                <Link to="/questionnaire" data-testid="final-cta" className="db-cta inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-base font-semibold" style={{ background: "#fff", color: "var(--db-forest)" }}>
                  Create My Personalised Plan <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <p className="mt-5 flex items-center justify-center gap-2 text-sm" style={{ opacity: 0.8 }}>
                <ShieldCheck className="w-4 h-4" /> Your food. Your routine. Your habits. Your plan.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ───────── Footer ───────── */}
      <footer className="border-t py-10 pb-28 md:pb-10" style={{ background: "#fff", borderColor: "var(--db-line)" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-5">
          <img src="/images/dietbanao-logo-horizontal.webp" alt="DietBanao" width="176" height="96" className="h-10 w-auto object-contain -ml-1" />
          <div className="flex flex-wrap justify-center gap-5 sm:gap-6 text-sm font-medium" style={{ color: "var(--db-char-soft)" }}>
            <Link to="/pricing" className="hover:opacity-70 transition">Pricing</Link>
            <Link to="/about-us" className="hover:opacity-70 transition">About</Link>
            <Link to="/contact" className="hover:opacity-70 transition">Contact</Link>
            <Link to="/blog" className="hover:opacity-70 transition">Blog</Link>
            <Link to="/privacy-policy" className="hover:opacity-70 transition">Privacy</Link>
            <Link to="/terms-and-conditions" className="hover:opacity-70 transition">Terms</Link>
          </div>
          <p className="text-xs" style={{ color: "var(--db-char-soft)" }}>© 2026 DietBanao. All rights reserved.</p>
        </div>
      </footer>

      {/* ───────── Sticky mobile CTA ───────── */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 px-4 py-3 border-t backdrop-blur-md" style={{ background: "rgba(248,244,236,0.95)", borderColor: "var(--db-line)" }}>
        <Link to="/questionnaire" data-testid="sticky-cta" className="db-cta flex items-center justify-center gap-2 w-full px-6 py-3.5 rounded-full text-base font-semibold text-white" style={{ background: "var(--db-forest)", boxShadow: "0 10px 24px -12px rgba(35,102,65,0.7)" }}>
          Create My Personalised Plan <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
};

export default LandingPage;
